import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { Icon } from "../Icon/Icon.js";
import { useDismiss } from "../useDismiss/useDismiss.js";
import styles from "./ShellNav.module.scss";

/** A rendered nav entry: a destination leaf, or a dropdown group of destination leaves. */
export type ShellNavItem =
  | { kind: "leaf"; label: string; value: string }
  | { kind: "group"; label: string; children: readonly { label: string; value: string }[] };

export interface ShellNavProps {
  items: readonly ShellNavItem[];
  /**
   * Is this destination the active one? A callback rather than an `active` value because the shells
   * decide differently — the artist shell matches a ROUTE prefix, the roster shell compares a tab KEY.
   */
  isActive: (value: string) => boolean;
  onSelect: (value: string) => void;
  /** Names the nav for assistive tech — "Primary", "Roster tabs". */
  label: string;
  /**
   * Extra DOM attributes for one destination, on both the flat tab and the menu item. The roster passes
   * `data-scoped-empty` for tabs that need an artist nobody has picked yet.
   */
  itemProps?: (value: string) => Record<string, string | undefined> | undefined;
  /**
   * Changes whenever the shell navigates. Any change closes an open menu — needed because navigation can
   * happen WITHOUT a click in here (browser back, a link on the page), which would otherwise leave a menu
   * hanging open over a screen it no longer describes.
   */
  closeKey?: string;
  /** Render the loading placeholder instead of the items. */
  loading?: boolean;
  className?: string;
}

/**
 * The shell top-nav strip: flat leaves plus dropdown GROUPS, shared by every shell that has one.
 *
 * Extracted 2026-08-31 from three near-copies (artist / roster / org). The duplication was not
 * cosmetic — it was carrying a style ruling unevenly. STYLE-GUIDE ruling 20 sets nav tabs and dropdown
 * items at **13px / 600 / `--ac-tracking-tight`** and names `--ac-fs-lg` (14px) plus a weightless
 * `.menuItem` as precisely the bug it corrected; the artist shell had been fixed and the other two still
 * carried the pre-ruling values. One component is what stops that happening again.
 *
 * The shells keep what legitimately differs — their brand lockup, their actions row, and which items they
 * pass. Everything below the item list is identical by construction.
 *
 * NOT built on `ActionMenu`, deliberately: that is a different contract, not a near-miss. Its items are
 * fire-and-forget ACTIONS with no active state, it opens on click only, and it hangs off a trigger control
 * rather than living in a `<nav>`. These items are DESTINATIONS carrying `aria-current`, the group button
 * lights when a child is current, and it opens on hover. Different element, different interaction.
 *
 * Menu behaviour, all three of which were load-bearing in the original and are easy to lose:
 * - Hover opens and closes; a click ENSURE-opens rather than toggling, because the click lands after
 *   `mouseenter` has already opened the menu and a toggle would close it on the same gesture.
 * - Escape and an outside pointer-press close a click-opened menu (`useDismiss`), which touch and keyboard
 *   users depend on — they never produce the `mouseleave` that closes it for a mouse.
 * - The group's own pill lights when any CHILD is active, so the active destination is still visible when
 *   its menu is shut.
 */
export function ShellNav({
  items, isActive, onSelect, label, itemProps, closeKey, loading, className,
}: ShellNavProps) {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  useDismiss(Boolean(openGroup), () => setOpenGroup(null), navRef);
  useEffect(() => { setOpenGroup(null); }, [closeKey]);

  return (
    <nav className={clsx(styles.nav, className)} aria-label={label} ref={navRef}>
      {loading && <span className={styles.skeleton} aria-hidden="true" />}
      {!loading && items.map((item) => {
        if (item.kind === "leaf") {
          return (
            <button
              key={item.value}
              type="button"
              className={styles.tab}
              aria-current={isActive(item.value) ? "page" : undefined}
              onClick={() => onSelect(item.value)}
              {...itemProps?.(item.value)}
            >
              {item.label}
            </button>
          );
        }

        const childActive = item.children.some((c) => isActive(c.value));
        const open = openGroup === item.label;
        return (
          <div
            key={item.label}
            className={styles.group}
            // Mouse enter/leave, not pointer events: pointerType gating proved unreliable (and always
            // reads "touch" under devtools emulation). Touch's emulated mouseenter is harmless — a tap
            // should open the menu anyway.
            onMouseEnter={() => setOpenGroup(item.label)}
            onMouseLeave={() => setOpenGroup((g) => (g === item.label ? null : g))}
          >
            <button
              type="button"
              className={clsx(styles.tab, styles.tabGroup)}
              aria-current={childActive ? "page" : undefined}
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={() => setOpenGroup(item.label)}
            >
              {item.label}
              <Icon icon={ChevronDown} size={16} />
            </button>
            {open && (
              <ul className={styles.menu} role="menu" aria-label={item.label}>
                {item.children.map((child) => (
                  <li key={child.value} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      aria-current={isActive(child.value) ? "page" : undefined}
                      onClick={() => {
                        setOpenGroup(null);
                        onSelect(child.value);
                      }}
                      {...itemProps?.(child.value)}
                    >
                      {child.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}
